import {Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Subject, takeUntil } from "rxjs";
import { OpportunityService } from "../../../../core/services/opportunity.service";
import { BolUploadOrderDetails, BolFileUploadResponse } from "../../../../core/types/bol-upload.types";
import { EntityResponse } from "../../../../core/types/types";
import { HttpErrorResponse } from "@angular/common/http";
import { ModalService } from "../../../../core/services/modal.service";
import {ErrorService} from '../../../../core/services/error.service';
import {BolUpload} from "../../../../core/validators/form-max-lengths";

interface UploadedFile {
    fileId: string;
    fileName: string;
    file: File;
}

@Component({
    selector: "app-bol-upload",
    standalone: true,
    imports: [CommonModule],
    templateUrl: "./bol-upload.component.html",
    styleUrl: "./bol-upload.component.scss",
})
export class BolUploadComponent implements OnInit, OnDestroy {
    @Input() onAuthSuccess?: () => void;

    private _errorService = inject(ErrorService);

    orderDetails: BolUploadOrderDetails | null = null;
    uploadedFiles: UploadedFile[] = [];
    isUploading: boolean = false;
    errorMessage: string = '';

    readonly MAX_FILES = 10;
    private readonly MAX_FILE_SIZE = BolUpload.MaxSizeBytes;
    private readonly ALLOWED_EXTENSIONS = BolUpload.AllowedExtensions;

    private _opportunityService = inject(OpportunityService);
    private _modalService = inject(ModalService);
    private _destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.fetchOrderDetails();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    fetchOrderDetails(): void {
        this._opportunityService
            .getBolUploadOrderDetails()
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (response: EntityResponse<BolUploadOrderDetails>) => {
                    this.orderDetails = response.data;
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage = error.error?.title
                        || error.error?.message
                        || error.message
                        || 'Unable to load order details. Please try again later.';
                    this._errorService.setError(errorMessage);
                    this.onError(error);
                }
            });
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }

        const files = Array.from(input.files);
        this.processFiles(files);

        // Reset input so the same file can be selected again
        input.value = '';
    }

    processFiles(files: File[]): void {
        // Check if adding these files would exceed the max limit
        if (this.uploadedFiles.length >= this.MAX_FILES) {
            this.errorMessage = 'Maximum number of BOL files has been uploaded already.';
            return;
        }

        if (this.uploadedFiles.length + files.length > this.MAX_FILES) {
            this.errorMessage = 'Maximum number of BOL files has been uploaded already.';
            return;
        }

        this.errorMessage = '';

        for (const file of files) {
            const extension = this.getFileExtension(file.name);
            if (!this.ALLOWED_EXTENSIONS.includes(extension as typeof BolUpload.AllowedExtensions[number])) {
                this.errorMessage = BolUpload.InvalidExtensionMessage;
                continue;
            }

            if (file.size > this.MAX_FILE_SIZE) {
                this.errorMessage = BolUpload.OversizeMessage;
                continue;
            }

            this.uploadFile(file);
        }
    }

    uploadFile(file: File): void {
        this.isUploading = true;

        this._opportunityService
            .uploadBolFile(file)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (response: EntityResponse<BolFileUploadResponse>) => {
                    this.uploadedFiles.push({
                        fileId: response.data.fileId,
                        fileName: response.data.fileName,
                        file: file
                    });
                    this.isUploading = false;
                },
                error: (error: HttpErrorResponse) => {
                    this.isUploading = false;
                    this.onError(error);
                }
            });
    }

    deleteFile(uploadedFile: UploadedFile): void {
        this._opportunityService
            .deleteBolFile(uploadedFile.fileId)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this.uploadedFiles = this.uploadedFiles.filter(f => f.fileId !== uploadedFile.fileId);
                    this.errorMessage = '';
                },
                error: (error: HttpErrorResponse) => {
                    this.onError(error);
                }
            });
    }

    getFileExtension(filename: string): string {
        return filename.split('.').pop()?.toLowerCase() || '';
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    onError(error: HttpErrorResponse): void {
        this._modalService.showError(error.error?.title, error.error?.message);
    }

    get canAddMoreFiles(): boolean {
        return this.uploadedFiles.length < this.MAX_FILES;
    }

    get originContent(): string | undefined {
        return this.orderDetails?.origin?.replace(/;/g, '<br>');
    }

    get destinationContent(): string | undefined {
        return this.orderDetails?.destination?.replace(/;/g, '<br>');
    }
}

